import {
    useEffect,
    useId,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
    type MouseEvent as ReactMouseEvent,
} from "react";
import { Box, Button } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import type { Editor } from "@tiptap/react";

import {
    linkBarSx,
    linkBarButtonSx,
    linkBarHeaderSx,
    linkBarHeaderTitleSx,
    nativePuckInputSx,
    nativePuckLabelSx,
} from "./richTextStyles";

type RichTextLinkBarProps = {
    editor: Editor | null;
    open: boolean;
    onClose: () => void;
};

export const RichTextLinkBar = (props: RichTextLinkBarProps) => {
    const { editor, open, onClose } = props;
    const reactId = useId();
    const textInputId = `${reactId}-text`;
    const urlInputId = `${reactId}-url`;

    const [text, setText] = useState("");
    const [url, setUrl] = useState("");
    const [isEditingExisting, setIsEditingExisting] = useState(false);
    const textInputRef = useRef<HTMLInputElement>(null);
    const urlInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open || !editor) return;

        const linkAttrs = editor.getAttributes("link");
        const existingHref = (linkAttrs.href as string | undefined) ?? "";
        const isOnExistingLink = Boolean(existingHref);

        if (isOnExistingLink) {
            editor.chain().extendMarkRange("link").run();
        }
        const { from, to } = editor.state.selection;
        const initialText = editor.state.doc.textBetween(from, to, " ");

        setText(initialText);
        setUrl(existingHref);
        setIsEditingExisting(isOnExistingLink);

        const focusTarget = initialText ? urlInputRef : textInputRef;
        const timer = setTimeout(() => focusTarget.current?.focus(), 50);
        return () => clearTimeout(timer);
    }, [open, editor]);

    if (!open || !editor) return null;

    // All click handlers stop propagation defensively so the click events do
    // not bubble up to any Puck CMS handler that may interpret them as a
    // panel-level submit / dismiss.
    const handleApply = (e?: ReactMouseEvent<HTMLButtonElement>) => {
        e?.preventDefault();
        e?.stopPropagation();

        const trimmedUrl = url.trim();
        if (!trimmedUrl) return;

        const finalText = text.trim() || trimmedUrl;

        editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .insertContent({
                type: "text",
                text: finalText,
                marks: [{ type: "link", attrs: { href: trimmedUrl } }],
            })
            // After insertContent the cursor sits at the end of the new link.
            // @tiptap/extension-link sets `inclusive: true` whenever
            // `autolink: true` (see Link.inclusive() in the extension source),
            // so that position is still considered "inside" the link mark —
            // the toolbar stays in link mode and the next typed character
            // would continue the link. Clearing the stored marks on the
            // transaction forces the editor out of link mode so the user can
            // immediately keep typing plain text after applying the link.
            .command(({ tr }) => {
                tr.setStoredMarks([]);
                return true;
            })
            .run();

        onClose();
    };

    const handleRemove = (e: ReactMouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        onClose();
    };

    const handleCancel = (e: ReactMouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
    };

    const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            handleApply();
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onClose();
        }
    };

    return (
        <Box onKeyDown={handleKeyDown}>
            <Box sx={linkBarHeaderSx}>
                <LinkIcon sx={{ fontSize: "16px", color: "var(--puck-color-grey-05)" }} />
                <Box component="span" sx={linkBarHeaderTitleSx}>
                    {isEditingExisting ? "Edit link" : "Insert link"}
                </Box>
            </Box>
            <Box sx={linkBarSx}>
                <Box sx={{ gridArea: "text" }}>
                    <Box component="label" htmlFor={textInputId} sx={nativePuckLabelSx}>
                        {"Text"}
                    </Box>
                    <Box
                        component="input"
                        ref={textInputRef}
                        id={textInputId}
                        type="text"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Display text"
                        autoComplete="off"
                        sx={nativePuckInputSx}
                    />
                </Box>

                <Box sx={{ gridArea: "url" }}>
                    <Box component="label" htmlFor={urlInputId} sx={nativePuckLabelSx}>
                        {"URL"}
                    </Box>
                    <Box
                        component="input"
                        ref={urlInputRef}
                        id={urlInputId}
                        type="url"
                        inputMode="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://example.com"
                        autoComplete="off"
                        sx={nativePuckInputSx}
                    />
                </Box>

                <Box
                    sx={{
                        gridArea: "actions",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                    }}
                >
                    {isEditingExisting && (
                        <Button type="button" onClick={handleRemove} disableRipple sx={linkBarButtonSx("danger")}>
                            {"Remove"}
                        </Button>
                    )}
                    <Button type="button" onClick={handleCancel} disableRipple sx={linkBarButtonSx("secondary")}>
                        {"Cancel"}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleApply}
                        disabled={!url.trim()}
                        disableRipple
                        sx={linkBarButtonSx("primary")}
                    >
                        {"Apply"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default RichTextLinkBar;
